import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Animated,
  Linking,
  Modal,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";
import { useTranslation } from "react-i18next";
import { observer } from "mobx-react";
import { runInAction } from "mobx";
import { v4 as uuidv4 } from "uuid";

import Colors from "../../../../Resources/Color";
import Fonts from "../../../../Resources/Fonts";
import GlobalStyles from "../../../../Resources/Styles";
import CustomAlert from "../../../Components/CustomAlert";
import FloatingMenu from "../../../Components/FloatingMenu";
import SearchableDropdown from "../../../Components/SearchableDropdown";
import { useToast } from "../../../contexts/ToastContext";
import useChevronRotation from "../../../hooks/useChevronRotation";
import { useDevice, useScreenDimensions } from "../../../_layout";
import { useModalKeyboardShortcuts } from "../../../platform/guards/modalKeyboardShortcuts";
import NutritionFood from "../../../Firestore/nutrition/NutritionFood";
import { NutritionEntitySyncStatus } from "../../../Firestore/nutrition/OptimisticNutritionEntity";
import {
  getLocalizedFoodName,
  getResolvedFoodType,
  hasExplicitFoodType,
  isCatalogFood,
} from "../foodCatalog";
import NutritionDraftStatusPill from "../../shared/NutritionDraftStatusPill";
import NutritionSyncNotice from "../../shared/NutritionSyncNotice";
import { getLocalizedAllergenLabel } from "../../shared/localizedAllergens";
import { COACH_NUTRITION_BOOTSTRAP_RESOURCES } from "../../coachNutritionBootstrap";
import {
  MAX_NUTRITION_AMOUNT_INPUT_LENGTH,
  MAX_NUTRITION_MEASURE_GRAMS_INPUT_LENGTH,
} from "../../nutritionLimits";
import {
  formatIntegerInput,
  formatMicronutrientInput,
  getInitialFoodQuantityInputValue,
  formatSingleDecimalInput,
  getAddFoodModalSubmitIssues,
  getResolvedFoodDraftValues,
  normalizeDecimalInput,
  normalizeWebsiteUrl,
} from "./foodDraft";
import { resolveAddFoodModalSaveResult } from "./addFoodModalSaveResult";
import {
  CANONICAL_ALLERGEN_CODES,
  getFoodSuitabilityOption,
  normalizeAllergenCodes,
} from "../foodClassification";
import {
  createEmptyMicronutrients,
  MICRONUTRIENT_FIELDS as MICRO_FIELDS,
} from "../../shared/micronutrients";
import {
  getBuiltInCommonMeasureItems,
  getInlineCommonMeasureLabel,
  getLocalizedCommonMeasureDisplayLabelForMeasure,
  normalizeBuiltInCommonMeasureId,
} from "../../shared/commonMeasures";

const createInitialMicros = () => createEmptyMicronutrients("");

const resolveInitialType = (food) => {
  if (hasExplicitFoodType(food)) {
    return food.type;
  }

  return getResolvedFoodType(food);
};

function getEditableFoodSuitabilityOption(food) {
  const classification = food && typeof food === "object" ? food : {};
  const isComplete =
    classification.dietaryDataComplete ?? classification.dietary_data_complete;

  if (isComplete !== true) {
    return "";
  }

  return getFoodSuitabilityOption(food);
}

function normalizeFoodId(value) {
  return String(value ?? "").trim().toLowerCase();
}

function isOptimisticFoodEntity(value) {
  return (
    value?.entityType === "food" &&
    typeof value?.applyDraft === "function" &&
    typeof value?.saveNow === "function" &&
    typeof value?.discardLocalChanges === "function"
  );
}

const createInitialFormState = (food = null, language = "en") => {
  const initialCommonMeasures = getInitialCommonMeasures(food);
  const initialQuantityUnitKey =
    String(
      food?.quantityUnitKey ??
        food?.selected_measure_id ??
        food?.selectedMeasureId ??
        "grams",
    ).trim() || "grams";
  const normalizedQuantityUnitKey =
    initialQuantityUnitKey === "grams"
      ? "grams"
      : normalizeBuiltInCommonMeasureId(initialQuantityUnitKey);
  const resolvedQuantityUnitKey =
    normalizedQuantityUnitKey !== "grams" &&
    initialCommonMeasures.some((measure) => measure.measureKey === normalizedQuantityUnitKey)
      ? normalizedQuantityUnitKey
      : "grams";

  return {
    name: getLocalizedFoodName(food, language) || "",
    type: resolveInitialType(food),
    quantity: getInitialFoodQuantityInputValue({
      quantity: food?.quantity,
      quantityReferenceG: food?.quantityReferenceG,
      reference_quantity_g: food?.reference_quantity_g,
      quantityUnitKey: resolvedQuantityUnitKey,
      selected_measure_id: food?.selected_measure_id,
      selectedMeasureId: food?.selectedMeasureId,
      commonMeasures: initialCommonMeasures,
    }),
    quantityUnitKey: resolvedQuantityUnitKey,
    energy: food?.calories != null ? formatIntegerInput(food.calories) : "",
    protein: food?.protein != null ? formatSingleDecimalInput(food.protein) : "",
    carbs: food?.carbs != null ? formatSingleDecimalInput(food.carbs) : "",
    fats: food?.fats != null ? formatSingleDecimalInput(food.fats) : "",
    fiber: food?.fiber != null ? formatSingleDecimalInput(food.fiber) : "",
    website: typeof food?.website === "string" ? food.website : "",
    allergens: normalizeAllergenCodes(food?.allergens ?? food?.allergen_codes),
    allergenDataComplete:
      food?.allergenDataComplete ?? food?.allergen_data_complete ?? false,
    dietarySuitability: getEditableFoodSuitabilityOption(food),
    microsExpanded: false,
    micros: getInitialMicrosValues(food),
    commonMeasures: initialCommonMeasures,
  };
};

function getInitialMicrosValues(food) {
  const initialMicros = createInitialMicros();
  const source =
    food?.micros && typeof food.micros === "object"
      ? food.micros
      : food?.micros_per_100g && typeof food.micros_per_100g === "object"
        ? food.micros_per_100g
        : {};

  MICRO_FIELDS.forEach(({ key }) => {
    if (source[key] == null || source[key] === "") {
      return;
    }

    initialMicros[key] = formatMicronutrientInput(source[key]);
  });

  return initialMicros;
}

function getInitialCommonMeasures(food) {
  const sourceMeasures = Array.isArray(food?.commonMeasures)
    ? food.commonMeasures
    : Array.isArray(food?.common_measures)
      ? food.common_measures
      : [];
  const seenIds = new Set();

  return sourceMeasures.reduce((measures, measure) => {
    const measureKey = normalizeBuiltInCommonMeasureId(
      measure?.measureKey ?? measure?.id,
    );
    const grams = formatSingleDecimalInput(measure?.grams);

    if (!measureKey || !grams || seenIds.has(measureKey)) {
      return measures;
    }

    seenIds.add(measureKey);

    measures.push({
      id: String(measure?.id ?? measureKey),
      measureKey,
      grams,
    });

    return measures;
  }, []);
}

const SectionCard = ({
  icon,
  iconColor,
  title,
  description,
  titleStyle = null,
  headerAction = null,
  children,
}) => (
  <View style={styles.sectionCard}>
    <View
      style={[
        styles.sectionHeader,
        headerAction ? styles.sectionHeaderSplit : null,
      ]}
    >
      <View style={styles.sectionHeaderMain}>
        <View
          style={[
            styles.sectionIconWrap,
            { backgroundColor: `${iconColor}12` },
          ]}
        >
          <Ionicons name={icon} size={15} color={iconColor} />
        </View>
        <View style={styles.sectionHeaderText}>
          <Text style={[styles.sectionTitle, titleStyle]}>{title}</Text>
          {description ? (
            <Text style={styles.sectionDescription}>{description}</Text>
          ) : null}
        </View>
      </View>
      {headerAction ? (
        <View style={styles.sectionHeaderAction}>{headerAction}</View>
      ) : null}
    </View>
    <View style={styles.sectionBody}>{children}</View>
  </View>
);

const AddFoodModal = observer(function AddFoodModal({
  isVisible,
  onClose,
  onSaved,
  coach,
  food = null,
}) {
  const { t, i18n } = useTranslation();
  const { showToast } = useToast();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();
  const language = i18n?.resolvedLanguage || i18n?.language || "en";
  const isCatalogSource = isCatalogFood(food);
  const isEditing = Boolean(food) && !isCatalogSource;
  const isCreatingFromCatalog = Boolean(food) && isCatalogSource;
  const baseForm = useMemo(
    () => createInitialFormState(food, language),
    [food, language],
  );
  const draftFoodId = useMemo(() => food?.id ?? uuidv4(), [food?.id]);
  const storeFoodEntity = useMemo(() => {
    if (isCatalogSource) {
      return null;
    }

    if (isOptimisticFoodEntity(food)) {
      return food;
    }

    const normalizedFoodId = normalizeFoodId(food?.id);
    if (!normalizedFoodId) {
      return null;
    }

    return (
      coach?.nutritionStore?.foodEntitiesById?.get?.(normalizedFoodId) ?? null
    );
  }, [coach?.nutritionStore, food, isCatalogSource]);
  const transientFoodEntity = useMemo(() => {
    if (storeFoodEntity) {
      return null;
    }

    const nextEntity = new NutritionFood(
      {
        id: draftFoodId,
        createdAt: food?.createdAt ?? null,
        updatedAt: food?.updatedAt ?? null,
      },
      {
        coach,
        store: coach?.nutritionStore,
      },
    );

    nextEntity.origin = isCatalogSource
      ? { catalog_food_id: food?.id ?? food?.code ?? null }
      : (food?.origin ?? null);
    nextEntity.brand = food?.brand ?? null;
    nextEntity.store = food?.store ?? null;
    nextEntity.initializeFromServerSnapshot(baseForm, {
      rev: food?._rev ?? 0,
    });

    return nextEntity;
  }, [
    baseForm,
    coach,
    draftFoodId,
    food,
    isCatalogSource,
    storeFoodEntity,
  ]);
  const foodEntity = storeFoodEntity ?? transientFoodEntity;
  const modalOwnsFoodEntity = !storeFoodEntity;
  const macroInputRefs = useRef({});
  const microInputRefs = useRef({});
  const measureInputRefs = useRef({});
  const nameInputRef = useRef(null);
  const quantityInputRef = useRef(null);
  const websiteInputRef = useRef(null);
  const pendingMeasureFocusIdRef = useRef(null);
  const suppressNextModalCancelRef = useRef(0);
  const [floatingPortalRoot, setFloatingPortalRoot] = useState(null);
  const [hoveredRemoveMeasureId, setHoveredRemoveMeasureId] = useState(null);
  const [hoveredInputKey, setHoveredInputKey] = useState(null);
  const [hoveredTypeOptionKey, setHoveredTypeOptionKey] = useState(null);
  const [hoveredAllergenCode, setHoveredAllergenCode] = useState(null);
  const [hoveredPreferenceKey, setHoveredPreferenceKey] = useState(null);
  const [isMicrosToggleHovered, setIsMicrosToggleHovered] = useState(false);
  const [hoveredQuantityUnitTrigger, setHoveredQuantityUnitTrigger] =
    useState(false);
  const [isQuantityUnitMenuOpen, setIsQuantityUnitMenuOpen] = useState(false);
  const [isAddMeasureMenuOpen, setIsAddMeasureMenuOpen] = useState(false);
  const [closeHovered, setCloseHovered] = useState(false);
  const [deleteHovered, setDeleteHovered] = useState(false);
  const [cancelHovered, setCancelHovered] = useState(false);
  const [confirmHovered, setConfirmHovered] = useState(false);
  const [didAttemptSubmit, setDidAttemptSubmit] = useState(false);
  const [isDeleteAlertVisible, setIsDeleteAlertVisible] = useState(false);
  const form = foodEntity?.draft ?? baseForm;

  useEffect(() => {
    if (isVisible !== true) {
      return;
    }

    coach?.ensureNutritionResourcesFromCloud?.(
      COACH_NUTRITION_BOOTSTRAP_RESOURCES.addFoodModal,
    )?.catch?.(() => {});
  }, [coach, isVisible]);

  const quantityUnitChevronRotation = useChevronRotation(
    isQuantityUnitMenuOpen,
    "closedDownOpenUp",
  );
  const microsChevronRotation = useChevronRotation(
    form.microsExpanded,
    "closedDownOpenUp",
  );
  const trackFoodEntity = useCallback(() => {
    if (!foodEntity?.id || !modalOwnsFoodEntity || !coach?.nutritionStore) {
      return;
    }

    runInAction(() => {
      coach.nutritionStore.foodEntitiesById.set(foodEntity.id, foodEntity);
    });
  }, [coach?.nutritionStore, foodEntity, modalOwnsFoodEntity]);
  const removeOwnedFoodEntity = useCallback(() => {
    if (!foodEntity?.id || !modalOwnsFoodEntity || !coach?.nutritionStore) {
      foodEntity?.dispose?.();
      return;
    }

    const trackedEntity = coach.nutritionStore.foodEntitiesById.get(foodEntity.id);
    if (trackedEntity && trackedEntity !== foodEntity) {
      foodEntity.dispose?.();
      return;
    }

    if (trackedEntity === foodEntity) {
      runInAction(() => {
        coach.nutritionStore.foodEntitiesById.delete(foodEntity.id);
      });
    }

    foodEntity.dispose?.();
  }, [coach?.nutritionStore, foodEntity, modalOwnsFoodEntity]);
  const applyFormUpdate = useCallback(
    (nextFormOrUpdater) => {
      if (!foodEntity) {
        return;
      }

      const nextForm =
        typeof nextFormOrUpdater === "function"
          ? nextFormOrUpdater(foodEntity.draft ?? baseForm)
          : nextFormOrUpdater;

      foodEntity.applyDraft(nextForm, {
        scheduleSave: false,
      });
    },
    [baseForm, foodEntity],
  );
  const handleFloatingPortalHostRef = useCallback((node) => {
    setFloatingPortalRoot((current) =>
      current === node ? current : node || null,
    );
  }, []);

  useEffect(() => {
    if (!isVisible) {
      setIsQuantityUnitMenuOpen(false);
      setIsAddMeasureMenuOpen(false);
      setDidAttemptSubmit(false);
      setIsDeleteAlertVisible(false);
    }
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      if (String(form.name ?? "").trim() !== "") {
        return;
      }
      nameInputRef.current?.focus?.();
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [isVisible]);

  useEffect(() => {
    if (!isVisible || !pendingMeasureFocusIdRef.current) {
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      const measureId = pendingMeasureFocusIdRef.current;
      const input = measureInputRefs.current?.[measureId];
      input?.focus?.();
      pendingMeasureFocusIdRef.current = null;
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [form.commonMeasures, isVisible]);

  const updateField = useCallback((key, value) => {
    applyFormUpdate((current) => ({
      ...current,
      [key]: value,
    }));
  }, [applyFormUpdate]);

  const updateMicroField = useCallback((key, value) => {
    applyFormUpdate((current) => ({
      ...current,
      micros: {
        ...current.micros,
        [key]: normalizeDecimalInput(value),
      },
    }));
  }, [applyFormUpdate]);

  const handleNumericFieldChange = useCallback(
    (key, value) => {
      updateField(key, normalizeDecimalInput(value));
    },
    [updateField],
  );
  const formatFieldOnBlur = useCallback((key, formatter) => {
    applyFormUpdate((current) => ({
      ...current,
      [key]: formatter(current[key]),
    }));
  }, [applyFormUpdate]);
  const formatMicroFieldOnBlur = useCallback((key) => {
    applyFormUpdate((current) => ({
      ...current,
      micros: {
        ...current.micros,
        [key]: formatMicronutrientInput(current.micros[key]),
      },
    }));
  }, [applyFormUpdate]);
  const formatMeasureFieldOnBlur = useCallback((measureId) => {
    applyFormUpdate((current) => ({
      ...current,
      commonMeasures: current.commonMeasures.map((measure) =>
        measure.id === measureId
          ? {
              ...measure,
              grams: formatSingleDecimalInput(measure.grams),
            }
          : measure,
      ),
    }));
  }, [applyFormUpdate]);
  const normalizeWebsiteFieldOnBlur = useCallback(() => {
    applyFormUpdate((current) => {
      const trimmedWebsite = String(current.website ?? "").trim();
      const normalizedWebsite = normalizeWebsiteUrl(trimmedWebsite);
      const nextWebsite = normalizedWebsite || trimmedWebsite;

      return nextWebsite === current.website
        ? current
        : {
            ...current,
            website: nextWebsite,
          };
    });
  }, [applyFormUpdate]);
  const focusMacroInput = useCallback((key) => {
    macroInputRefs.current?.[key]?.focus?.();
  }, []);
  const focusMicroInput = useCallback((key) => {
    microInputRefs.current?.[key]?.focus?.();
  }, []);
  const focusQuantityInput = useCallback(() => {
    quantityInputRef.current?.focus?.();
  }, []);
  const focusEnergyInput = useCallback(() => {
    macroInputRefs.current?.energy?.focus?.();
  }, []);
  const focusWebsiteInput = useCallback(() => {
    websiteInputRef.current?.focus?.();
  }, []);
  const focusNextMacroInput = useCallback((currentKey) => {
    const macroFieldOrder = ["energy", "protein", "carbs", "fats", "fiber"];
    const currentIndex = macroFieldOrder.indexOf(currentKey);
    if (currentIndex === -1) {
      return;
    }

    const nextKey = macroFieldOrder[currentIndex + 1];
    if (!nextKey) {
      return;
    }

    macroInputRefs.current?.[nextKey]?.focus?.();
  }, []);
  const focusNextMicroInput = useCallback((currentKey) => {
    const currentIndex = MICRO_FIELDS.findIndex(
      (field) => field.key === currentKey,
    );
    if (currentIndex === -1) {
      return;
    }

    const nextKey = MICRO_FIELDS[currentIndex + 1]?.key;
    if (nextKey) {
      microInputRefs.current?.[nextKey]?.focus?.();
      return;
    }

    focusWebsiteInput();
  }, [focusWebsiteInput]);
  const focusNextEmptyMeasureInput = useCallback(
    (currentMeasureId) => {
      const currentIndex = form.commonMeasures.findIndex(
        (measure) => measure.id === currentMeasureId,
      );
      if (currentIndex === -1) {
        return;
      }

      const orderedCandidates = [
        ...form.commonMeasures.slice(currentIndex + 1),
        ...form.commonMeasures.slice(0, currentIndex),
      ];
      const nextEmptyMeasure = orderedCandidates.find(
        (measure) => normalizeDecimalInput(measure?.grams).trim() === "",
      );

      if (!nextEmptyMeasure) {
        return;
      }

      measureInputRefs.current?.[nextEmptyMeasure.id]?.focus?.();
    },
    [form.commonMeasures],
  );

  const {
    normalizedWebsite,
    hasWebsiteValue,
    isWebsiteValid,
  } = useMemo(() => getResolvedFoodDraftValues(form), [form]);
  const submitIssues = useMemo(() => getAddFoodModalSubmitIssues(form), [form]);
  const modalTitle = isEditing
    ? t("editFood-string")
    : isCreatingFromCatalog
      ? t("catalogFoodModalTitle-string")
      : t("addFoodModalTitle-string");

  const commonMeasureItems = useMemo(
    () => getBuiltInCommonMeasureItems(t),
    [t],
  );

  const availableMeasureItems = useMemo(
    () =>
      commonMeasureItems.filter(
        (item) =>
          !form.commonMeasures.some(
            (measure) => measure.measureKey === item.id,
          ),
      ),
    [commonMeasureItems, form.commonMeasures],
  );
  const quantityUnitOptions = useMemo(
    () => [
      { id: "grams", label: "g" },
      ...form.commonMeasures.map((measure) => ({
        id: measure.measureKey,
        label: getLocalizedCommonMeasureDisplayLabelForMeasure(measure, { t }),
      })),
    ],
    [form.commonMeasures, t],
  );
  const selectedQuantityUnitLabel =
    quantityUnitOptions.find((option) => option.id === form.quantityUnitKey)
      ?.label ?? "g";

  const addMeasure = useCallback((item) => {
    if (!item?.id) {
      return;
    }

    const measureId = uuidv4();
    applyFormUpdate((current) => {
      if (
        current.commonMeasures.some((measure) => measure.measureKey === item.id)
      ) {
        return current;
      }

      pendingMeasureFocusIdRef.current = measureId;

      return {
        ...current,
        commonMeasures: [
          ...current.commonMeasures,
          {
            id: measureId,
            measureKey: item.id,
            grams: "",
          },
        ],
      };
    });
  }, [applyFormUpdate]);

  const updateMeasureGrams = useCallback((measureId, value) => {
    const normalizedValue = normalizeDecimalInput(value);

    applyFormUpdate((current) => ({
      ...current,
      commonMeasures: current.commonMeasures.map((measure) =>
        measure.id === measureId
          ? {
              ...measure,
              grams: normalizedValue,
            }
          : measure,
      ),
    }));
  }, [applyFormUpdate]);

  const removeMeasure = useCallback((measureId) => {
    applyFormUpdate((current) => {
      const measureToRemove = current.commonMeasures.find(
        (measure) => measure.id === measureId,
      );
      return {
        ...current,
        quantityUnitKey:
          current.quantityUnitKey === measureToRemove?.measureKey
            ? "grams"
            : current.quantityUnitKey,
        commonMeasures: current.commonMeasures.filter(
          (measure) => measure.id !== measureId,
        ),
      };
    });
  }, [applyFormUpdate]);

  const toggleAllergenSelection = useCallback((allergenCode) => {
    applyFormUpdate((current) => {
      const currentAllergens = Array.isArray(current.allergens)
        ? current.allergens
        : [];
      const hasAllergen = currentAllergens.includes(allergenCode);
      const nextAllergens = hasAllergen
        ? currentAllergens.filter((entry) => entry !== allergenCode)
        : normalizeAllergenCodes([...currentAllergens, allergenCode]);

      return {
        ...current,
        allergens: nextAllergens,
        allergenDataComplete: nextAllergens.length > 0,
      };
    });
  }, [applyFormUpdate]);

  const selectNoAllergens = useCallback(() => {
    applyFormUpdate((current) => ({
      ...current,
      allergens: [],
      allergenDataComplete: true,
    }));
  }, [applyFormUpdate]);

  const dietarySuitabilityOptions = useMemo(
    () => [
      {
        key: "none",
        label: t("none-string", "None"),
      },
      {
        key: "vegetarian",
        label: t("vegetarian-string", "Vegetarian"),
      },
      {
        key: "vegan",
        label: t("vegan-string", "Vegan"),
      },
    ],
    [t],
  );

  const isSaveActionDisabled =
    !foodEntity ||
    foodEntity?.syncStatus === NutritionEntitySyncStatus.SAVING ||
    (isEditing && !foodEntity?.hasUnsyncedChanges);
  const isDeleteActionDisabled =
    !isEditing ||
    !foodEntity?.id ||
    foodEntity?.syncStatus === NutritionEntitySyncStatus.SAVING;
  const shouldHighlightValidation = didAttemptSubmit;
  const isNameTitleInvalid = shouldHighlightValidation && submitIssues.name;
  const isQuantityTitleInvalid =
    shouldHighlightValidation && submitIssues.quantity;
  const isAllergensTitleInvalid =
    shouldHighlightValidation && submitIssues.allergens;
  const isPreferencesTitleInvalid =
    shouldHighlightValidation && submitIssues.preferences;
  const isCommonMeasuresTitleInvalid =
    shouldHighlightValidation && submitIssues.commonMeasures;

  const handleSave = useCallback(async () => {
    if (isSaveActionDisabled || !foodEntity) {
      return;
    }

    const wasEditing = isEditing;
    setDidAttemptSubmit(true);

    if (submitIssues.hasRequiredIssues) {
      showToast("requiredInfo-string", {
        type: "warning",
        title: "requiredInfo-string",
        description: "nutritionFoodRequiredFields-string",
        duration: 3000,
      });
      return;
    }

    if (submitIssues.website) {
      showToast("invalidUrl-string", {
        type: "warning",
        title: "invalidUrl-string",
        description: "nutritionFoodInvalidUrlDescription-string",
        duration: 3000,
      });
      return;
    }

    if (!foodEntity.hasUnsyncedChanges) {
      foodEntity.applyDraft(foodEntity.getDraftSnapshot(), {
        touchedPaths: ["name"],
        scheduleSave: false,
      });
    }

    trackFoodEntity();
    const savePromise = foodEntity.saveNow();
    onClose?.();

    const result = await savePromise;
    const trackedFood =
      coach?.nutritionStore?.foodEntitiesById?.get?.(
        String(foodEntity.id ?? "").trim().toLowerCase(),
      ) ?? foodEntity;
    const {
      savedFood,
      shouldShowOfflinePendingToast,
    } = resolveAddFoodModalSaveResult({
      result,
      trackedFood,
      fallbackFood: foodEntity,
    });

    if (savedFood) {
      onSaved?.(savedFood);
    }

    if (shouldShowOfflinePendingToast) {
      showToast("autosaveOfflinePending-string", {
        type: "warning",
        title: "autosaveOfflinePending-string",
        description: "nutritionFoodOfflinePendingToast-string",
      });
      return;
    }

    if (savedFood) {
      const savedFoodName =
        String(savedFood?.name ?? "").trim() ||
        getLocalizedFoodName(savedFood, language) ||
        t("food-string", "Food");

      const titleKey = wasEditing
        ? "nutritionFoodUpdatedTitle-string"
        : "nutritionFoodCreatedTitle-string";
      const descriptionKey = wasEditing
        ? "nutritionFoodUpdatedDescription-string"
        : "nutritionFoodCreatedDescription-string";

      showToast(titleKey, {
        type: "info",
        title: titleKey,
        description: descriptionKey,
        variables: {
          name: savedFoodName,
        },
      });
    }

    if (savedFood || result === false) {
      return;
    }

    showToast("autosaveError-string", {
      type: "error",
      title: "autosaveError-string",
      description: "nutritionFoodSaveErrorToastDescription-string",
    });
  }, [
    coach?.nutritionStore?.foodEntitiesById,
    foodEntity,
    isEditing,
    isSaveActionDisabled,
    language,
    onClose,
    onSaved,
    showToast,
    submitIssues,
    t,
    trackFoodEntity,
  ]);
  const handleOpenWebsite = useCallback(async () => {
    if (!normalizedWebsite) {
      return;
    }

    try {
      await Linking.openURL(normalizedWebsite);
    } catch (error) {
      console.warn("Failed to open website URL:", normalizedWebsite, error);
    }
  }, [normalizedWebsite]);
  const closeVisibleFloatingElement = useCallback(() => {
    if (isAddMeasureMenuOpen) {
      setIsAddMeasureMenuOpen(false);
      return true;
    }

    if (isQuantityUnitMenuOpen) {
      setIsQuantityUnitMenuOpen(false);
      return true;
    }

    return false;
  }, [isAddMeasureMenuOpen, isQuantityUnitMenuOpen]);

  const isFloatingElementVisible =
    isAddMeasureMenuOpen || isQuantityUnitMenuOpen;

  const handleDeletePress = useCallback(() => {
    if (isDeleteActionDisabled) {
      return;
    }

    closeVisibleFloatingElement();
    setIsDeleteAlertVisible(true);
  }, [closeVisibleFloatingElement, isDeleteActionDisabled]);
  const handleDeleteAlertClose = useCallback(() => {
    setIsDeleteAlertVisible(false);
  }, []);
  const handleConfirmDelete = useCallback(async () => {
    const foodId = String(foodEntity?.id ?? "").trim();
    const deleteFoodName =
      String(form.name ?? "").trim() ||
      getLocalizedFoodName(foodEntity, language) ||
      t("food-string", "Food");

    if (!foodId || !coach?.deleteNutritionFood) {
      return;
    }

    try {
      await coach.deleteNutritionFood(foodId);
      setIsDeleteAlertVisible(false);
      foodEntity?.discardLocalChanges?.();
      removeOwnedFoodEntity();
      onClose?.();
      showToast("nutritionFoodDeleteSuccessTitle-string", {
        type: "info",
        title: "nutritionFoodDeleteSuccessTitle-string",
        description: "nutritionFoodDeleteSuccessDescription-string",
        variables: {
          name: deleteFoodName,
        },
      });
    } catch (error) {
      console.error("Failed to delete food:", error);
      showToast("nutritionFoodDeleteErrorTitle-string", {
        type: "error",
        title: "nutritionFoodDeleteErrorTitle-string",
        description: "nutritionCommonTryAgainLater-string",
      });
      throw error;
    }
  }, [
    coach,
    foodEntity,
    form.name,
    language,
    onClose,
    removeOwnedFoodEntity,
    showToast,
    t,
  ]);

  const suppressNextModalCancel = useCallback(() => {
    suppressNextModalCancelRef.current = Date.now() + 250;
  }, []);

  const handleAddMeasureMenuOpenChange = useCallback(
    (nextOpen, _event, reason) => {
      if (nextOpen === false && reason === "escape-key") {
        suppressNextModalCancel();
      }

      setIsAddMeasureMenuOpen(nextOpen);
    },
    [suppressNextModalCancel],
  );

  useEffect(() => {
    if (
      !isVisible ||
      !isFloatingElementVisible ||
      typeof window === "undefined"
    ) {
      return undefined;
    }

    const handleFloatingEscape = (event) => {
      if (event?.key !== "Escape") {
        return;
      }

      suppressNextModalCancel();
      event?.preventDefault?.();
      event?.stopPropagation?.();
      event?.stopImmediatePropagation?.();
      closeVisibleFloatingElement();
    };

    window.addEventListener("keydown", handleFloatingEscape, true);
    return () => {
      window.removeEventListener("keydown", handleFloatingEscape, true);
    };
  }, [
    closeVisibleFloatingElement,
    isFloatingElementVisible,
    isVisible,
    suppressNextModalCancel,
  ]);

  const handleCancel = useCallback(() => {
    if (Date.now() < suppressNextModalCancelRef.current) {
      return;
    }

    if (closeVisibleFloatingElement()) {
      return;
    }

    foodEntity?.discardLocalChanges?.();
    removeOwnedFoodEntity();
    onClose?.();
  }, [closeVisibleFloatingElement, foodEntity, onClose, removeOwnedFoodEntity]);

  useModalKeyboardShortcuts({
    isVisible,
    onConfirm: handleSave,
    onCancel: handleCancel,
    isConfirmEnabled: !isSaveActionDisabled,
    allowEnterOnSingleLineInput: false,
    preserveEscapeException: isFloatingElementVisible,
  });

  const handleClose = useCallback(() => {
    foodEntity?.discardLocalChanges?.();
    removeOwnedFoodEntity();
    onClose?.();
  }, [foodEntity, onClose, removeOwnedFoodEntity]);
  const deleteFoodName =
    String(form.name ?? "").trim() ||
    getLocalizedFoodName(foodEntity, language) ||
    t("food-string", "Food");
  const syncNotice = useMemo(() => {
    if (foodEntity?.syncStatus === NutritionEntitySyncStatus.OFFLINE_PENDING) {
      return {
        tone: "warning",
        title: t("autosaveOfflinePending-string"),
        message: t(
          "nutritionFoodOfflinePendingNotice-string",
          "This save is queued offline. Keep the modal open or close it safely; it will sync when the connection returns.",
        ),
      };
    }

    if (
      foodEntity?.lastError &&
      foodEntity?.syncStatus !== NutritionEntitySyncStatus.OFFLINE_PENDING
    ) {
      return {
        tone:
          foodEntity?.syncStatus === NutritionEntitySyncStatus.ERROR
            ? "error"
            : "warning",
        title: t("autosaveError-string"),
        message: t(
          "nutritionFoodSaveErrorNotice-string",
          "We couldn't save this food. Try saving again.",
        ),
        actionLabel: t("save-string"),
        onAction: () => foodEntity?.saveNow?.(),
      };
    }

    return null;
  }, [
    foodEntity?.lastError,
    foodEntity?.saveNow,
    foodEntity?.syncStatus,
    t,
  ]);

  const macroCards = [
    {
      key: "energy",
      label: t("energy-string"),
      value: form.energy,
      onChangeText: (value) => handleNumericFieldChange("energy", value),
      onBlur: () => formatFieldOnBlur("energy", formatIntegerInput),
      unit: "kcal",
    },
    {
      key: "protein",
      label: t("protein-string"),
      value: form.protein,
      onChangeText: (value) => handleNumericFieldChange("protein", value),
      onBlur: () => formatFieldOnBlur("protein", formatSingleDecimalInput),
      unit: "g",
    },
    {
      key: "carbs",
      label: t("carbs-string"),
      value: form.carbs,
      onChangeText: (value) => handleNumericFieldChange("carbs", value),
      onBlur: () => formatFieldOnBlur("carbs", formatSingleDecimalInput),
      unit: "g",
    },
    {
      key: "fats",
      label: t("fats-string"),
      value: form.fats,
      onChangeText: (value) => handleNumericFieldChange("fats", value),
      onBlur: () => formatFieldOnBlur("fats", formatSingleDecimalInput),
      unit: "g",
    },
    {
      key: "fiber",
      label: t("fiber-string"),
      value: form.fiber,
      onChangeText: (value) => handleNumericFieldChange("fiber", value),
      onBlur: () => formatFieldOnBlur("fiber", formatSingleDecimalInput),
      unit: "g",
    },
  ];

  const isSmallScreen = screenSize === "small";
  const isMediumScreen = screenSize === "medium";

  return (
    <Modal
      animationType="fade"
      transparent={true}
      visible={isVisible}
      onRequestClose={handleCancel}
    >
      <View
        ref={handleFloatingPortalHostRef}
        collapsable={false}
        dataSet={{ efortcoachFloatingRoot: "true" }}
        style={GlobalStyles.centeredView}
      >
        <TouchableOpacity
          onPress={handleClose}
          style={GlobalStyles.touchableBackground}
        />

        <View
          style={[
            styles.modalView,
            isSmallScreen ? styles.modalViewSmall : null,
            isMediumScreen ? styles.modalViewMedium : null,
          ]}
        >
          <TouchableOpacity
            activeOpacity={0.8}
            onPress={handleClose}
            onMouseEnter={() => !isMobile && setCloseHovered(true)}
            onMouseLeave={() => setCloseHovered(false)}
            style={styles.closeButton}
          >
            <Ionicons
              name="close"
              size={22}
              color={closeHovered ? Colors.darkgrayColor : Colors.grayColor}
            />
          </TouchableOpacity>

          <View
            style={[styles.header, isSmallScreen ? styles.headerSmall : null]}
          >
            <View
              style={[
                styles.headerRow,
                isSmallScreen ? styles.headerRowSmall : null,
              ]}
            >
              <Text style={styles.title}>{modalTitle}</Text>
              <NutritionDraftStatusPill session={foodEntity} />
            </View>
          </View>

          <NutritionSyncNotice
            session={foodEntity}
            tone={syncNotice?.tone}
            title={syncNotice?.title}
            message={syncNotice?.message}
            actionLabel={syncNotice?.actionLabel}
            onAction={syncNotice?.onAction}
            style={styles.syncNotice}
          />

          <ScrollView
            style={[
              styles.scrollView,
              !isMobile ? styles.scrollViewDesktop : null,
            ]}
            contentContainerStyle={[
              styles.scrollContent,
              isSmallScreen ? styles.scrollContentSmall : null,
            ]}
            keyboardShouldPersistTaps="handled"
            showsVerticalScrollIndicator={!isMobile}
          >
            <View style={styles.topSectionCard}>
              <View style={styles.identityRow}>
                <View style={styles.nameColumn}>
                  <Text
                    style={[
                      styles.fieldLabel,
                      isNameTitleInvalid ? styles.labelTextInvalid : null,
                    ]}
                  >
                    {t("addFoodModalNameLabel-string")}
                    <Text style={styles.requiredAsterisk}>*</Text>
                  </Text>
                  <View
                    onMouseEnter={
                      !isMobile ? () => setHoveredInputKey("name") : undefined
                    }
                    onMouseLeave={
                      !isMobile ? () => setHoveredInputKey(null) : undefined
                    }
                    style={[
                      styles.inputShell,
                      hoveredInputKey === "name"
                        ? styles.inputShellHovered
                        : null,
                    ]}
                  >
                    <TextInput
                      ref={nameInputRef}
                      value={form.name}
                      onChangeText={(value) => updateField("name", value)}
                      onSubmitEditing={focusQuantityInput}
                      placeholder={t("addFoodModalNamePlaceholder-string")}
                      placeholderTextColor={Colors.grayColor}
                      maxLength={80}
                      returnKeyType="next"
                      blurOnSubmit={false}
                      style={styles.input}
                    />
                  </View>
                </View>

                <View style={styles.quantityColumn}>
                  <Text
                    style={[
                      styles.fieldLabel,
                      isQuantityTitleInvalid ? styles.labelTextInvalid : null,
                    ]}
                  >
                    {t("quantity-string")}
                    <Text style={styles.requiredAsterisk}>*</Text>
                  </Text>
                  <View
                    onMouseEnter={
                      !isMobile
                        ? () => setHoveredInputKey("quantity")
                        : undefined
                    }
                    onMouseLeave={
                      !isMobile ? () => setHoveredInputKey(null) : undefined
                    }
                    style={[
                      styles.inputShell,
                      hoveredInputKey === "quantity"
                        ? styles.inputShellHovered
                        : null,
                    ]}
                  >
                    <TextInput
                      ref={quantityInputRef}
                      value={form.quantity}
                      onChangeText={(value) =>
                        handleNumericFieldChange("quantity", value)
                      }
                      onBlur={() =>
                        formatFieldOnBlur("quantity", formatSingleDecimalInput)
                      }
                      onSubmitEditing={focusEnergyInput}
                      placeholder="100"
                      placeholderTextColor={Colors.grayColor}
                      inputMode="decimal"
                      maxLength={MAX_NUTRITION_AMOUNT_INPUT_LENGTH}
                      returnKeyType="next"
                      blurOnSubmit={false}
                      selectTextOnFocus={true}
                      style={styles.input}
                    />
                    {form.commonMeasures.length > 0 ? (
                      <View style={styles.quantityUnitDropdownWrap}>
                        <FloatingMenu
                          items={quantityUnitOptions}
                          open={isQuantityUnitMenuOpen}
                          onOpenChange={setIsQuantityUnitMenuOpen}
                          portalRoot={floatingPortalRoot}
                          placement="bottom-end"
                          width={180}
                          maxHeight={220}
                          triggerContainerStyle={
                            styles.quantityUnitFloatingTriggerContainer
                          }
                          trigger={
                            <View
                              onMouseEnter={
                                !isMobile
                                  ? () => setHoveredQuantityUnitTrigger(true)
                                  : undefined
                              }
                              onMouseLeave={() =>
                                setHoveredQuantityUnitTrigger(false)
                              }
                              style={styles.quantityUnitTrigger}
                            >
                              <View style={styles.quantityUnitTriggerLabelWrap}>
                                <Text
                                  numberOfLines={1}
                                  ellipsizeMode="tail"
                                  style={styles.quantityUnitTriggerText}
                                >
                                  {selectedQuantityUnitLabel || "g"}
                                </Text>
                              </View>
                              <Animated.View
                                style={{
                                  transform: [{ rotate: quantityUnitChevronRotation }],
                                }}
                              >
                                <Ionicons
                                  name="chevron-down"
                                  size={12}
                                  color={
                                    hoveredQuantityUnitTrigger
                                      ? Colors.darkgrayColor
                                      : Colors.grayColor
                                  }
                                />
                              </Animated.View>
                            </View>
                          }
                          renderItem={({ item, close }) => {
                            const isSelected =
                              form.quantityUnitKey === item?.id;
                            return (
                              <TouchableOpacity
                                activeOpacity={0.8}
                                onPress={() => {
                                  updateField(
                                    "quantityUnitKey",
                                    item?.id ?? "grams",
                                  );
                                  close?.();
                                }}
                                style={styles.quantityUnitMenuItem}
                              >
                                <Text
                                  style={[
                                    styles.quantityUnitMenuItemText,
                                    isSelected
                                      ? styles.quantityUnitMenuItemTextSelected
                                      : null,
                                  ]}
                                >
                                  {item?.label}
                                </Text>
                              </TouchableOpacity>
                            );
                          }}
                        />
                      </View>
                    ) : (
                      <Text style={styles.inputUnit}>g</Text>
                    )}
                  </View>
                </View>
              </View>

              <View style={styles.typeSection}>
                <Text style={styles.fieldLabel}>
                  {t("addFoodModalTypeLabel-string")}
                </Text>
                <View style={styles.typeGrid}>
                  {[
                    {
                      key: "food",
                      label: t("food-string"),
                    },
                    {
                      key: "supplement",
                      label: t("supplement-string"),
                      icon: "flask-outline",
                    },
                  ].map((option) => {
                    const isActive = form.type === option.key;
                    return (
                      <TouchableOpacity
                        key={option.key}
                        activeOpacity={0.85}
                        onPress={() => updateField("type", option.key)}
                        onMouseEnter={() =>
                          !isMobile && setHoveredTypeOptionKey(option.key)
                        }
                        onMouseLeave={() => setHoveredTypeOptionKey(null)}
                        style={styles.typeOptionTouchable}
                      >
                        <View
                          style={[
                            styles.typeOption,
                            hoveredTypeOptionKey === option.key && !isActive
                              ? styles.typeOptionHovered
                              : null,
                            isActive ? styles.typeOptionActive : null,
                            {
                              transition:
                                "border-color 200ms ease-in-out, background-color 200ms ease-in-out",
                            },
                          ]}
                        >
                          <View
                            style={[
                              styles.typeIconWrap,
                              isActive ? styles.typeIconWrapActive : null,
                            ]}
                          >
                            <Ionicons
                              name={
                                option.key === "food"
                                  ? "nutrition-outline"
                                  : option.icon
                              }
                              size={16}
                              color={
                                isActive ? Colors.bubbleColor : Colors.grayColor
                              }
                            />
                          </View>
                          <Text
                            style={[
                              styles.typeOptionLabel,
                              isActive ? styles.typeOptionLabelActive : null,
                            ]}
                          >
                            {option.label}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    );
                  })}
                </View>
              </View>
            </View>

            <View style={[styles.selectionSection, styles.allergensSection]}>
              <Text
                style={[
                  styles.selectionSectionTitle,
                  isAllergensTitleInvalid ? styles.labelTextInvalid : null,
                ]}
              >
                {t(
                  "nutritionFoodContainsAllergens-string",
                  "Contains allergens",
                )}
                <Text style={styles.requiredAsterisk}>*</Text>
              </Text>
              <View style={styles.allergenSelectionGrid}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={selectNoAllergens}
                  onMouseEnter={
                    !isMobile ? () => setHoveredAllergenCode("none") : undefined
                  }
                  onMouseLeave={
                    !isMobile ? () => setHoveredAllergenCode(null) : undefined
                  }
                  style={styles.allergenSelectionTouchable}
                >
                  <View
                    style={[
                      styles.allergenSelectionChip,
                      styles.allergenSelectionChipNone,
                      hoveredAllergenCode === "none" &&
                      !(form.allergenDataComplete && form.allergens.length === 0)
                        ? styles.allergenSelectionChipHovered
                        : null,
                      form.allergenDataComplete && form.allergens.length === 0
                        ? styles.allergenSelectionChipNoneActive
                        : null,
                    ]}
                  >
                    <Text
                      style={[
                        styles.allergenSelectionChipText,
                        styles.allergenSelectionChipTextNone,
                        form.allergenDataComplete && form.allergens.length === 0
                          ? styles.allergenSelectionChipTextNoneActive
                          : null,
                      ]}
                    >
                      {t("none-string", "None")}
                    </Text>
                  </View>
                </TouchableOpacity>
                <View style={styles.allergenSelectionDivider} />
                {CANONICAL_ALLERGEN_CODES.map((allergenCode) => {
                  const isSelected = form.allergens.includes(allergenCode);

                  return (
                    <TouchableOpacity
                      key={allergenCode}
                      activeOpacity={0.85}
                      onPress={() => toggleAllergenSelection(allergenCode)}
                      onMouseEnter={
                        !isMobile
                          ? () => setHoveredAllergenCode(allergenCode)
                          : undefined
                      }
                      onMouseLeave={
                        !isMobile ? () => setHoveredAllergenCode(null) : undefined
                      }
                      style={styles.allergenSelectionTouchable}
                    >
                      <View
                        style={[
                          styles.allergenSelectionChip,
                          hoveredAllergenCode === allergenCode && !isSelected
                            ? styles.allergenSelectionChipHovered
                            : null,
                          isSelected
                            ? styles.allergenSelectionChipActive
                            : null,
                        ]}
                      >
                        <Text
                          style={[
                            styles.allergenSelectionChipText,
                            isSelected
                              ? styles.allergenSelectionChipTextActive
                              : null,
                          ]}
                        >
                          {getLocalizedAllergenLabel(allergenCode, t)}
                        </Text>
                      </View>
                    </TouchableOpacity>
                  );
                })}
              </View>
            </View>

            <View style={styles.selectionSection}>
              <Text
                style={[
                  styles.selectionSectionTitle,
                  isPreferencesTitleInvalid ? styles.labelTextInvalid : null,
                ]}
              >
                {t(
                  "nutritionFoodDietarySuitability-string",
                  "Dietary suitability",
                )}
                <Text style={styles.requiredAsterisk}>*</Text>
              </Text>
              <View style={styles.preferenceChipRow}>
                {dietarySuitabilityOptions.map((option) => {
                  const isActive = form.dietarySuitability === option.key;
                  const showDivider = option.key === "vegetarian";
                  const isNoneOption = option.key === "none";

                  return (
                    <React.Fragment key={option.key}>
                      {showDivider ? (
                        <View style={styles.preferenceSelectionDivider} />
                      ) : null}
                      <TouchableOpacity
                        activeOpacity={0.85}
                        onPress={() =>
                          updateField("dietarySuitability", option.key)
                        }
                        onMouseEnter={
                          !isMobile
                            ? () => setHoveredPreferenceKey(option.key)
                            : undefined
                        }
                        onMouseLeave={
                          !isMobile
                            ? () => setHoveredPreferenceKey(null)
                            : undefined
                        }
                        style={styles.dietaryOptionTouchable}
                      >
                        <View
                          style={[
                            styles.preferenceChip,
                            isNoneOption ? styles.preferenceChipNone : null,
                            hoveredPreferenceKey === option.key && !isActive
                              ? styles.preferenceChipHovered
                              : null,
                            isNoneOption && isActive
                              ? styles.preferenceChipNoneActive
                              : null,
                            isActive ? styles.preferenceChipActive : null,
                          ]}
                        >
                          <Text
                            style={[
                              styles.preferenceChipText,
                              isNoneOption
                                ? styles.preferenceChipTextNone
                                : null,
                              isNoneOption && isActive
                                ? styles.preferenceChipTextNoneActive
                                : null,
                              isActive ? styles.preferenceChipTextActive : null,
                            ]}
                          >
                            {option.label}
                          </Text>
                        </View>
                      </TouchableOpacity>
                    </React.Fragment>
                  );
                })}
              </View>
            </View>

            <View style={styles.minimalNutritionSection}>
              <View style={styles.nutritionSectionHeader}>
                <View style={styles.nutritionSectionLine} />
                <Text style={styles.nutritionSectionLabel}>
                  {t("per100g-string")}
                </Text>
                <View style={styles.nutritionSectionLine} />
              </View>
              <View style={styles.macroRows}>
                {macroCards.map((card) => (
                  <View
                    key={card.key}
                    style={[
                      styles.macroRow,
                      {
                        width: isSmallScreen
                          ? "calc((100% - 8px) / 2)"
                          : "calc((100% - 32px) / 5)",
                      },
                    ]}
                  >
                    <Text style={styles.macroRowLabel}>{card.label}</Text>
                    <TouchableOpacity
                      activeOpacity={0.92}
                      onPress={() => focusMacroInput(card.key)}
                      onMouseEnter={
                        !isMobile
                          ? () => setHoveredInputKey(`macro-${card.key}`)
                          : undefined
                      }
                      onMouseLeave={
                        !isMobile ? () => setHoveredInputKey(null) : undefined
                      }
                      style={styles.macroRowTouchable}
                    >
                      <View
                        style={[
                          styles.macroRowInputShell,
                          hoveredInputKey === `macro-${card.key}`
                            ? styles.macroRowInputShellHovered
                            : null,
                        ]}
                      >
                        <TextInput
                          ref={(node) => {
                            macroInputRefs.current[card.key] = node;
                          }}
                          value={card.value}
                          onChangeText={card.onChangeText}
                          onBlur={card.onBlur}
                          onSubmitEditing={() => focusNextMacroInput(card.key)}
                          inputMode="decimal"
                          placeholder="0"
                          placeholderTextColor={Colors.grayColor}
                          returnKeyType={card.key === "fiber" ? "done" : "next"}
                          blurOnSubmit={card.key === "fiber"}
                          selectTextOnFocus={true}
                          style={styles.macroRowInput}
                        />
                        <Text style={styles.macroRowUnit}>{card.unit}</Text>
                      </View>
                    </TouchableOpacity>
                  </View>
                ))}
              </View>

              <View style={styles.microsMinimalWrap}>
                <TouchableOpacity
                  activeOpacity={0.85}
                  onPress={() =>
                    applyFormUpdate((current) => ({
                      ...current,
                      microsExpanded: !current.microsExpanded,
                    }))
                  }
                  onMouseEnter={
                    !isMobile ? () => setIsMicrosToggleHovered(true) : undefined
                  }
                  onMouseLeave={
                    !isMobile ? () => setIsMicrosToggleHovered(false) : undefined
                  }
                  style={styles.microsMinimalButton}
                >
                  <View style={styles.microsMinimalLabelRow}>
                    <Text
                      style={[
                        styles.microsMinimalLabel,
                        isMicrosToggleHovered
                          ? styles.microsMinimalLabelHovered
                          : null,
                      ]}
                    >
                      {t("addFoodModalMicrosTitle-string")}
                    </Text>
                    <Animated.View
                      style={[
                        styles.microsMinimalChevron,
                        { transform: [{ rotate: microsChevronRotation }] },
                      ]}
                    >
                      <Ionicons
                        name="chevron-down"
                        size={12}
                        color={
                          isMicrosToggleHovered
                            ? Colors.darkgrayColor
                            : Colors.grayColor
                        }
                      />
                    </Animated.View>
                  </View>
                </TouchableOpacity>

                {form.microsExpanded ? (
                  <View style={styles.microGrid}>
                    {MICRO_FIELDS.map((field) => (
                      <View
                        key={field.key}
                        style={[
                          styles.microCardColumn,
                          {
                            width: isSmallScreen
                              ? "100%"
                              : "calc((100% - 24px) / 3)",
                          },
                        ]}
                      >
                        <TouchableOpacity
                          activeOpacity={0.92}
                          onPress={() => focusMicroInput(field.key)}
                          onMouseEnter={
                            !isMobile
                              ? () => setHoveredInputKey(`micro-${field.key}`)
                              : undefined
                          }
                          onMouseLeave={
                            !isMobile
                              ? () => setHoveredInputKey(null)
                              : undefined
                          }
                          style={styles.microCardTouchable}
                        >
                          <View
                            style={[
                              styles.microCard,
                              hoveredInputKey === `micro-${field.key}`
                                ? styles.microCardHovered
                                : null,
                            ]}
                          >
                            <Text style={styles.microLabel}>
                              {t(field.labelKey)}
                            </Text>
                            <View style={styles.microInputShell}>
                              <TextInput
                                ref={(node) => {
                                  microInputRefs.current[field.key] = node;
                                }}
                                value={form.micros[field.key]}
                                onChangeText={(value) =>
                                  updateMicroField(field.key, value)
                                }
                                onBlur={() => formatMicroFieldOnBlur(field.key)}
                                onSubmitEditing={() =>
                                  focusNextMicroInput(field.key)
                                }
                                placeholder="0"
                                placeholderTextColor={Colors.grayColor}
                                inputMode="decimal"
                                returnKeyType={
                                  field.key ===
                                  MICRO_FIELDS[MICRO_FIELDS.length - 1]?.key
                                    ? "done"
                                    : "next"
                                }
                                blurOnSubmit={
                                  field.key ===
                                  MICRO_FIELDS[MICRO_FIELDS.length - 1]?.key
                                }
                                selectTextOnFocus={true}
                                style={styles.microInput}
                              />
                              <Text style={styles.microInputUnit}>
                                {field.unit}
                              </Text>
                            </View>
                          </View>
                        </TouchableOpacity>
                      </View>
                    ))}
                  </View>
                ) : null}
              </View>
            </View>

            <SectionCard
              icon="scale-outline"
              iconColor={Colors.bubbleColor}
              title={t("commonMeasures-string")}
              description={t("addFoodModalMeasuresDescription-string")}
              titleStyle={
                isCommonMeasuresTitleInvalid ? styles.labelTextInvalid : null
              }
              headerAction={
                <View
                  style={[
                    styles.measureHeaderActionWrap,
                    isSmallScreen ? styles.measureHeaderActionWrapSmall : null,
                  ]}
                >
                  <SearchableDropdown
                    items={availableMeasureItems}
                    triggerLabel={`+ ${t("addFoodModalMeasurePlaceholder-string")}`}
                    searchPlaceholder={t("addFoodModalMeasureSearch-string")}
                    emptyLabel={t("addFoodModalNoMeasuresFound-string")}
                    suggestionsLabel={t("commonMeasures-string")}
                    onSelect={addMeasure}
                    onEscapeKeyDown={suppressNextModalCancel}
                    open={isAddMeasureMenuOpen}
                    onOpenChange={handleAddMeasureMenuOpenChange}
                    portalRoot={floatingPortalRoot}
                    width={isSmallScreen ? 260 : 300}
                    maxHeight={240}
                    disabled={availableMeasureItems.length === 0}
                    triggerStyle={styles.measureHeaderTrigger}
                    renderTrigger={({ triggerText, isHovered, isDisabled }) => (
                      <View style={styles.measureHeaderTriggerContent}>
                        <Text
                          numberOfLines={1}
                          ellipsizeMode="tail"
                          style={[
                            styles.measureHeaderTriggerText,
                            isHovered && !isDisabled
                              ? styles.measureHeaderTriggerTextHovered
                              : null,
                          ]}
                        >
                          {triggerText}
                        </Text>
                      </View>
                    )}
                  />
                </View>
              }
            >
              {form.commonMeasures.length > 0 ? (
                <View style={styles.measureList}>
                  {form.commonMeasures.map((measure, index) => {
                    const isFirst = index === 0;
                    const isLast = index === form.commonMeasures.length - 1;
                    return (
                      <View key={measure.id} style={styles.measureRowContainer}>
                        <View style={styles.treeLineContainer}>
                          <View
                            style={[
                              styles.treeVerticalLine,
                              isFirst ? styles.treeVerticalLineFirst : null,
                              isLast ? styles.treeVerticalLineLast : null,
                            ]}
                          />
                          <View style={styles.treeHorizontalLine} />
                          <View style={styles.treeDot} />
                        </View>
                        <View style={styles.measureRow}>
                          <View style={styles.measureLabelWrap}>
                            <Text style={styles.measureLabelText}>
                              1 {getInlineCommonMeasureLabel(
                                getLocalizedCommonMeasureDisplayLabelForMeasure(measure, {
                                  t,
                                }),
                              )}
                            </Text>
                          </View>

                          <View style={styles.measureActionsWrap}>
                            <Text style={styles.measureEquals}>=</Text>
                            <View
                              onMouseEnter={
                                !isMobile
                                  ? () =>
                                      setHoveredInputKey(
                                        `measure-${measure.id}`,
                                      )
                                  : undefined
                              }
                              onMouseLeave={
                                !isMobile
                                  ? () => setHoveredInputKey(null)
                                  : undefined
                              }
                              style={[
                                styles.measureInputShell,
                                hoveredInputKey === `measure-${measure.id}`
                                  ? styles.measureInputShellHovered
                                  : null,
                              ]}
                            >
                              <TextInput
                                ref={(node) => {
                                  if (node) {
                                    measureInputRefs.current[measure.id] = node;
                                  } else {
                                    delete measureInputRefs.current[measure.id];
                                  }
                                }}
                                value={measure.grams}
                                onChangeText={(value) =>
                                  updateMeasureGrams(measure.id, value)
                                }
                                onBlur={() =>
                                  formatMeasureFieldOnBlur(measure.id)
                                }
                                onSubmitEditing={() =>
                                  focusNextEmptyMeasureInput(measure.id)
                                }
                                placeholder="0"
                                placeholderTextColor={Colors.grayColor}
                                inputMode="decimal"
                                maxLength={
                                  MAX_NUTRITION_MEASURE_GRAMS_INPUT_LENGTH
                                }
                                returnKeyType="next"
                                blurOnSubmit={false}
                                selectTextOnFocus={true}
                                style={styles.measureInput}
                              />
                              <Text style={styles.measureInputUnit}>g</Text>
                            </View>
                            <TouchableOpacity
                              activeOpacity={0.8}
                              onPress={() => removeMeasure(measure.id)}
                              onMouseEnter={
                                !isMobile
                                  ? () => setHoveredRemoveMeasureId(measure.id)
                                  : undefined
                              }
                              onMouseLeave={
                                !isMobile
                                  ? () => setHoveredRemoveMeasureId(null)
                                  : undefined
                              }
                              style={styles.removeMeasureButton}
                            >
                              <Ionicons
                                name="close"
                                size={18}
                                color={
                                  hoveredRemoveMeasureId === measure.id
                                    ? Colors.darkgrayColor
                                    : Colors.grayColor
                                }
                              />
                            </TouchableOpacity>
                          </View>
                        </View>
                      </View>
                    );
                  })}
                </View>
              ) : null}
            </SectionCard>

            <SectionCard
              icon="link-outline"
              iconColor={Colors.carbsColor}
              title={t("website-string")}
              description={t("addFoodModalWebsiteDescription-string")}
            >
              <View
                onMouseEnter={
                  !isMobile ? () => setHoveredInputKey("website") : undefined
                }
                onMouseLeave={
                  !isMobile ? () => setHoveredInputKey(null) : undefined
                }
                style={[
                  styles.inputShell,
                  hoveredInputKey === "website"
                    ? styles.inputShellHovered
                    : null,
                  hasWebsiteValue && !isWebsiteValid
                    ? styles.inputShellInvalid
                    : null,
                ]}
              >
                <TextInput
                  ref={websiteInputRef}
                  value={form.website}
                  onChangeText={(value) => updateField("website", value)}
                  onBlur={normalizeWebsiteFieldOnBlur}
                  placeholder={t("addFoodModalWebsitePlaceholder-string")}
                  placeholderTextColor={Colors.grayColor}
                  autoCapitalize="none"
                  autoCorrect={false}
                  keyboardType="url"
                  returnKeyType="done"
                  selectTextOnFocus={true}
                  accessibilityLabel={t("website-string")}
                  accessibilityState={{ invalid: !isWebsiteValid }}
                  style={styles.input}
                />
              </View>
              {hasWebsiteValue ? (
                <View style={styles.websiteMetaRow}>
                  {normalizedWebsite ? (
                    <TouchableOpacity
                      activeOpacity={0.8}
                      onPress={handleOpenWebsite}
                      accessibilityRole="link"
                      accessibilityLabel={normalizedWebsite}
                      style={styles.websiteLink}
                    >
                      <Ionicons
                        name="open-outline"
                        size={14}
                        color={Colors.bubbleColor}
                      />
                      <Text style={styles.websiteLinkText}>
                        {normalizedWebsite}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <Text style={styles.websiteValidationText}>
                      {t("invalidUrl-string")}
                    </Text>
                  )}
                </View>
              ) : null}
            </SectionCard>
          </ScrollView>

          <View
            style={[
              styles.actionsRow,
              isSmallScreen ? styles.actionsRowSmall : null,
            ]}
          >
            <View style={styles.footerDeleteSlot}>
              {isEditing ? (
                <TouchableOpacity
                  activeOpacity={0.85}
                  disabled={isDeleteActionDisabled}
                  onPress={handleDeletePress}
                  onMouseEnter={() => !isMobile && setDeleteHovered(true)}
                  onMouseLeave={() => setDeleteHovered(false)}
                  style={[
                    styles.deleteButton,
                    deleteHovered && !isDeleteActionDisabled
                      ? styles.deleteButtonHovered
                      : null,
                  ]}
                >
                  <Text
                    style={[
                      styles.deleteButtonText,
                      isDeleteActionDisabled
                        ? styles.deleteButtonTextDisabled
                        : null,
                    ]}
                  >
                    {t("delete-string", "Delete")}
                  </Text>
                </TouchableOpacity>
              ) : (
                <View style={styles.footerDeleteSpacer} />
              )}
            </View>

            <View style={styles.buttonCluster}>
              <TouchableOpacity
                activeOpacity={0.85}
                onPress={handleClose}
                onMouseEnter={() => !isMobile && setCancelHovered(true)}
                onMouseLeave={() => setCancelHovered(false)}
                style={[
                  styles.secondaryButton,
                  cancelHovered ? styles.secondaryButtonHovered : null,
                ]}
              >
                <Text style={styles.secondaryButtonText}>
                  {t("cancel-string")}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                activeOpacity={0.85}
                disabled={isSaveActionDisabled}
                onPress={handleSave}
                onMouseEnter={() => !isMobile && setConfirmHovered(true)}
                onMouseLeave={() => setConfirmHovered(false)}
                style={[
                  styles.primaryButton,
                  isSaveActionDisabled ? styles.primaryButtonDisabled : null,
                  !isSaveActionDisabled && confirmHovered
                    ? styles.primaryButtonHovered
                    : null,
                ]}
              >
                <Text style={styles.primaryButtonText}>
                  {isCreatingFromCatalog
                    ? t("duplicateAndSave-string")
                    : isEditing
                      ? t("save-string")
                      : t("create-string")}
                </Text>
              </TouchableOpacity>
            </View>
          </View>

          <CustomAlert
            isVisible={isDeleteAlertVisible}
            onClose={handleDeleteAlertClose}
            title="deleteCoachFoodAlertTitle-string"
            text="sureToDelete-string"
            textParams={{ itemName: deleteFoodName }}
            action={handleConfirmDelete}
            specialCTA="delete-string"
          />
        </View>
      </View>
    </Modal>
  );
});

const styles = StyleSheet.create({
  modalView: {
    width: 760,
    maxWidth: "92%",
    maxHeight: "90%",
    backgroundColor: Colors.whiteColor,
    borderRadius: 20,
    paddingTop: 24,
    paddingBottom: 20,
    boxShadow: "0px 16px 42px rgba(15, 23, 42, 0.16)",
  },
  modalViewMedium: {
    width: 680,
  },
  modalViewSmall: {
    width: "94%",
    maxHeight: "94%",
    paddingTop: 18,
    paddingBottom: 16,
  },
  closeButton: {
    position: "absolute",
    top: 16,
    right: 16,
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
    zIndex: 20,
  },
  header: {
    paddingHorizontal: 24,
    paddingRight: 56,
    marginBottom: 12,
  },
  headerSmall: {
    paddingHorizontal: 16,
    paddingRight: 48,
  },
  headerRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  headerRowSmall: {
    alignItems: "flex-start",
    flexDirection: "column",
  },
  title: {
    ...Fonts.bold18,
    textTransform: "uppercase",
    letterSpacing: 0.5,
  },
  syncNotice: {
    marginHorizontal: 24,
    marginBottom: 12,
  },
  scrollView: {
    flexGrow: 0,
  },
  scrollViewDesktop: {
    overflowY: "scroll",
    scrollbarGutter: "stable",
    scrollbarWidth: "auto",
    scrollbarColor: "auto",
  },
  scrollContent: {
    paddingHorizontal: 24,
    paddingBottom: 8,
    gap: 12,
  },
  scrollContentSmall: {
    paddingHorizontal: 16,
  },
  sectionCard: {
    borderRadius: 16,
    borderWidth: 0,
    backgroundColor: "transparent",
    paddingHorizontal: 0,
    paddingVertical: 8,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  sectionHeaderSplit: {
    justifyContent: "space-between",
    flexWrap: "wrap",
    gap: 12,
  },
  sectionHeaderMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  sectionIconWrap: {
    width: 30,
    height: 30,
    borderRadius: 10,
    alignItems: "center",
    justifyContent: "center",
  },
  sectionHeaderText: {
    flex: 1,
    minWidth: 0,
    gap: 3,
    justifyContent: "center",
  },
  sectionHeaderAction: {
    flexShrink: 1,
    minWidth: 0,
    maxWidth: "100%",
    alignItems: "flex-end",
  },
  sectionTitle: {
    ...Fonts.medium14,
  },
  sectionDescription: {
    ...Fonts.regular12,
    color: Colors.grayColor,
    lineHeight: 17,
  },
  sectionBody: {
    marginTop: 14,
    gap: 14,
  },
  topSectionCard: {
    borderRadius: 16,
    borderWidth: 0,
    backgroundColor: "transparent",
    paddingHorizontal: 0,
    paddingVertical: 0,
    gap: 14,
  },
  identityRow: {
    flexDirection: "row",
    gap: 12,
    flexWrap: "wrap",
  },
  nameColumn: {
    flex: 1.8,
    minWidth: 260,
  },
  quantityColumn: {
    flex: 0.8,
    minWidth: 140,
  },
  fieldLabel: {
    ...Fonts.medium12,
    color: Colors.darkgrayColor,
    marginBottom: 8,
  },
  labelTextInvalid: {
    color: Colors.redColor,
  },
  requiredAsterisk: {
    color: Colors.redColor,
  },
  selectionSection: {
    paddingVertical: 0,
    gap: 8,
  },
  allergensSection: {
    marginTop: 8,
  },
  selectionSectionTitle: {
    ...Fonts.medium12,
    color: Colors.darkgrayColor,
  },
  inputShell: {
    minHeight: 42,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    transition:
      "border-color 180ms ease-in-out, background-color 180ms ease-in-out",
  },
  inputShellHovered: {
    borderColor: Colors.grayColor,
  },
  inputShellInvalid: {
    borderColor: Colors.redColor,
    backgroundColor: "rgba(240, 68, 56, 0.04)",
  },
  input: {
    flex: 1,
    minWidth: 0,
    fontSize: 13,
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 10,
  },
  websiteMetaRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 10,
  },
  websiteLink: {
    minHeight: 22,
    maxWidth: "100%",
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  websiteLinkText: {
    ...Fonts.medium12,
    color: Colors.bubbleColor,
    textDecorationLine: "underline",
    flexShrink: 1,
  },
  websiteValidationText: {
    ...Fonts.regular12,
    color: Colors.redColor,
  },
  inputUnit: {
    ...Fonts.medium12,
    color: Colors.grayColor,
    marginLeft: 8,
  },
  quantityUnitDropdownWrap: {
    marginLeft: 8,
    flexShrink: 0,
    minWidth: 0,
    maxWidth: 160,
  },
  quantityUnitFloatingTriggerContainer: {
    width: "auto",
    maxWidth: "100%",
    alignItems: "flex-end",
  },
  quantityUnitTrigger: {
    minWidth: 0,
    minHeight: 22,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    gap: 4,
  },
  quantityUnitTriggerLabelWrap: {
    minWidth: 0,
    flexShrink: 1,
    alignItems: "flex-end",
    overflow: "hidden",
  },
  quantityUnitTriggerText: {
    ...Fonts.regular12,
    textAlign: "right",
    color: Colors.grayColor,
  },
  quantityUnitMenuItem: {
    width: "100%",
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  quantityUnitMenuItemText: {
    ...Fonts.regular13,
    color: Colors.blackColor,
  },
  quantityUnitMenuItemTextSelected: {
    color: Colors.bubbleColor,
    fontWeight: "500",
  },
  typeSection: {
    gap: 0,
  },
  typeGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 12,
  },
  typeOptionTouchable: {
    flex: 1,
    minWidth: 180,
    borderRadius: 14,
  },
  typeOption: {
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    padding: 14,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 10,
  },
  typeOptionHovered: {
    borderColor: Colors.grayColor,
  },
  typeOptionActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  typeIconWrap: {
    width: 34,
    height: 34,
    borderRadius: 12,
    backgroundColor: Colors.lightGrayColor,
    alignItems: "center",
    justifyContent: "center",
    transitionProperty: "background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  typeIconWrapActive: {
    backgroundColor: "rgba(63, 123, 141, 0.12)",
  },
  typeOptionLabel: {
    ...Fonts.medium13,
    color: Colors.blackColor,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  typeOptionLabelActive: {
    color: Colors.bubbleColor,
  },
  allergenSelectionGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  allergenSelectionTouchable: {
    borderRadius: 12,
  },
  allergenSelectionDivider: {
    width: 1,
    height: 18,
    borderRadius: 999,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 2,
  },
  allergenSelectionChip: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 10,
    paddingVertical: 6,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  allergenSelectionChipHovered: {
    borderColor: Colors.grayColor,
  },
  allergenSelectionChipActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  allergenSelectionChipNone: {
    backgroundColor: Colors.lightGrayColor,
  },
  allergenSelectionChipNoneActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  allergenSelectionChipText: {
    ...Fonts.medium12,
    fontSize: 11,
    color: Colors.darkgrayColor,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  allergenSelectionChipTextActive: {
    color: Colors.bubbleColor,
  },
  allergenSelectionChipTextNone: {
    color: Colors.darkgrayColor,
    fontWeight: "600",
  },
  allergenSelectionChipTextNoneActive: {
    color: Colors.bubbleColor,
  },
  preferenceChipRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 6,
  },
  dietaryOptionTouchable: {
    borderRadius: 12,
  },
  preferenceSelectionDivider: {
    width: 1,
    height: 18,
    borderRadius: 999,
    backgroundColor: Colors.borderColor,
    marginHorizontal: 2,
  },
  preferenceChip: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 10,
    paddingVertical: 6,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  preferenceChipNone: {
    backgroundColor: Colors.lightGrayColor,
  },
  preferenceChipHovered: {
    borderColor: Colors.grayColor,
  },
  preferenceChipNoneActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  preferenceChipActive: {
    borderColor: Colors.bubbleColor,
    backgroundColor: "rgba(63, 123, 141, 0.06)",
  },
  preferenceChipText: {
    ...Fonts.medium12,
    fontSize: 11,
    color: Colors.darkgrayColor,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  preferenceChipTextNone: {
    color: Colors.darkgrayColor,
    fontWeight: "600",
  },
  preferenceChipTextActive: {
    color: Colors.bubbleColor,
  },
  preferenceChipTextNoneActive: {
    color: Colors.bubbleColor,
  },
  minimalNutritionSection: {
    borderRadius: 16,
    borderWidth: 0,
    backgroundColor: "transparent",
    marginTop: 8,
    paddingHorizontal: 0,
    paddingVertical: 8,
    gap: 14,
  },
  nutritionSectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  nutritionSectionLabel: {
    ...Fonts.medium12,
    color: Colors.grayColor,
    fontWeight: "400",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    textAlign: "center",
    paddingHorizontal: 4,
  },
  nutritionSectionLine: {
    flex: 1,
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  macroRows: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  macroRow: {
    gap: 8,
  },
  macroRowLabel: {
    ...Fonts.medium12,
    color: Colors.darkgrayColor,
  },
  macroRowInputShell: {
    minHeight: 42,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    paddingHorizontal: 12,
    flexDirection: "row",
    alignItems: "center",
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  macroRowInputShellHovered: {
    borderColor: Colors.grayColor,
  },
  macroRowTouchable: {
    width: "100%",
    borderRadius: 12,
  },
  macroRowInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 10,
  },
  macroRowUnit: {
    ...Fonts.regular12,
    color: Colors.grayColor,
    marginLeft: 8,
  },
  microsMinimalWrap: {
    gap: 12,
  },
  microsMinimalButton: {
    minHeight: 24,
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
  },
  microsMinimalLabelRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
  },
  microsMinimalLabel: {
    ...Fonts.regular13,
    fontSize: 12,
    color: Colors.grayColor,
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  microsMinimalLabelHovered: {
    color: Colors.darkgrayColor,
  },
  microsMinimalChevron: {
    transitionProperty: "color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  measureHeaderActionWrap: {
    width: "auto",
    minWidth: 0,
    flexShrink: 1,
    maxWidth: "100%",
    alignItems: "flex-end",
  },
  measureHeaderActionWrapSmall: {
    maxWidth: "100%",
  },
  measureHeaderTrigger: {
    width: "auto",
    minWidth: 0,
    maxWidth: "100%",
    flexShrink: 1,
    minHeight: 22,
    borderWidth: 0,
    borderRadius: 0,
    backgroundColor: "transparent",
    paddingHorizontal: 0,
    paddingVertical: 0,
    justifyContent: "flex-end",
    overflow: "hidden",
  },
  measureHeaderTriggerContent: {
    minWidth: 0,
    maxWidth: "100%",
    flexShrink: 1,
  },
  measureHeaderTriggerText: {
    ...Fonts.medium13,
    color: Colors.bubbleColor,
    textAlign: "right",
    flexShrink: 1,
  },
  measureHeaderTriggerTextHovered: {
    color: Colors.darkBubbleColor,
  },
  measureList: {
    gap: 0,
  },
  measureRowContainer: {
    flexDirection: "row",
    alignItems: "stretch",
  },
  treeLineContainer: {
    width: 40,
    position: "relative",
  },
  treeVerticalLine: {
    position: "absolute",
    left: 14.5,
    top: 0,
    bottom: 0,
    width: 1,
    backgroundColor: Colors.borderColor,
  },
  treeVerticalLineFirst: {
    top: -14,
  },
  treeVerticalLineLast: {
    bottom: "50%",
  },
  treeHorizontalLine: {
    position: "absolute",
    left: 14.5,
    top: "50%",
    marginTop: -0.5,
    width: 14,
    height: 1,
    backgroundColor: Colors.borderColor,
  },
  treeDot: {
    position: "absolute",
    left: 27.5,
    top: "50%",
    marginTop: -3,
    width: 6,
    height: 6,
    borderRadius: 3,
    backgroundColor: Colors.whiteColor,
    borderWidth: 1.5,
    borderColor: Colors.bubbleColor,
  },
  measureRow: {
    flex: 1,
    paddingVertical: 10,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  measureLabelWrap: {
    flex: 1,
    minWidth: 120,
  },
  measureLabelText: {
    ...Fonts.medium14,
    color: Colors.blackColor,
  },
  measureActionsWrap: {
    flexDirection: "row",
    alignItems: "center",
    gap: 10,
  },
  measureEquals: {
    ...Fonts.regular14,
    color: Colors.grayColor,
  },
  measureInputShell: {
    width: 118,
    minHeight: 38,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    backgroundColor: Colors.whiteColor,
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 10,
    transition:
      "border-color 180ms ease-in-out, background-color 180ms ease-in-out",
  },
  measureInputShellHovered: {
    borderColor: Colors.grayColor,
  },
  measureInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 14,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 10,
    textAlign: "right",
  },
  measureInputUnit: {
    fontSize: 13,
    fontWeight: "400",
    color: Colors.grayColor,
    marginLeft: 4,
  },
  removeMeasureButton: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: "center",
    justifyContent: "center",
  },
  microGrid: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  microCardColumn: {
    minWidth: 0,
  },
  microCardTouchable: {
    width: "100%",
    borderRadius: 12,
  },
  microCard: {
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "transparent",
    backgroundColor: Colors.lightGrayColor,
    paddingHorizontal: 10,
    paddingVertical: 8,
    gap: 4,
    transitionProperty: "border-color, background-color",
    transitionDuration: "180ms",
    transitionTimingFunction: "ease-in-out",
  },
  microCardHovered: {
    borderColor: Colors.grayColor,
    backgroundColor: Colors.hoveredGray,
  },
  microLabel: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.darkgrayColor,
  },
  microInputShell: {
    minHeight: 20,
    flexDirection: "row",
    alignItems: "center",
  },
  microInput: {
    flex: 1,
    minWidth: 0,
    fontSize: 11,
    fontWeight: "400",
    color: Colors.blackColor,
    outlineStyle: "none",
    paddingVertical: 0,
  },
  microInputUnit: {
    fontSize: 10,
    fontWeight: "400",
    color: Colors.grayColor,
    marginLeft: 4,
  },
  actionsRow: {
    borderTopWidth: 1,
    borderTopColor: Colors.borderColor,
    paddingTop: 16,
    paddingHorizontal: 24,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  actionsRowSmall: {
    paddingHorizontal: 16,
    alignItems: "stretch",
  },
  footerDeleteSlot: {
    flex: 1,
    minWidth: 120,
    alignItems: "flex-start",
  },
  footerDeleteSpacer: {
    minHeight: 20,
  },
  deleteButton: {
    minHeight: 20,
    justifyContent: "center",
  },
  deleteButtonHovered: {
    opacity: 0.7,
  },
  deleteButtonText: {
    ...Fonts.medium13,
    color: Colors.redColor,
  },
  deleteButtonTextDisabled: {
    opacity: 0.45,
  },
  buttonCluster: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 10,
    marginLeft: "auto",
  },
  secondaryButton: {
    height: 38,
    paddingHorizontal: 16,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.whiteColor,
  },
  secondaryButtonHovered: {
    backgroundColor: Colors.backgroundColor,
  },
  secondaryButtonText: {
    ...Fonts.medium13,
    color: Colors.darkgrayColor,
  },
  primaryButton: {
    height: 38,
    paddingHorizontal: 18,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: Colors.bubbleColor,
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: Colors.bubbleColor,
  },
  primaryButtonHovered: {
    opacity: 0.9,
  },
  primaryButtonDisabled: {
    opacity: 0.55,
  },
  primaryButtonText: {
    ...Fonts.medium13,
    color: Colors.whiteColor,
  },
});

export default AddFoodModal;
